import React, { useEffect, useState, useRef } from 'react';
import { NewspaperIcon, ExternalLinkIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
const FeaturedPress = ({
  language = 'en'
}) => {
  const sectionRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const elements = entry.target.querySelectorAll('.fade-in, .slide-in-left, .slide-in-right');
          elements.forEach((el, index) => {
            setTimeout(() => {
              el.classList.add('active');
            }, index * 100);
          });
        }
      });
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const toggleArticle = () => {
    setIsExpanded(!isExpanded);
  };
  const translations = {
    en: {
      featured: 'Featured',
      press: 'Press',
      newspaper: 'The Telegram & Gazette',
      title: 'On Benefits of Raising Immigration Funding in Massachusetts',
      summary: "Published an opinion column discussing the economic and social benefits of increasing immigration funding in Massachusetts, highlighting the positive impact of immigrant communities on the state's economy and culture.",
      date: 'November 5, 2023',
      readOriginal: 'Read Original Article',
      expandArticle: 'Expand Full Article',
      collapseArticle: 'Collapse Article',
      fullArticle: 'Full Article',
      viewOriginal: 'View Original Article on Telegram.com'
    },
    vi: {
      featured: 'Báo Chí',
      press: 'Nổi Bật',
      newspaper: 'The Telegram & Gazette',
      title: 'Về Lợi Ích của Việc Tăng Ngân Sách cho Nhập Cư tại Massachusetts',
      summary: 'Đã xuất bản một bài viết ý kiến thảo luận về lợi ích kinh tế và xã hội của việc tăng ngân sách cho nhập cư tại Massachusetts, nhấn mạnh tác động tích cực của cộng đồng nhập cư đối với nền kinh tế và văn hóa của tiểu bang.',
      date: 'Ngày 5 tháng 11 năm 2023',
      readOriginal: 'Đọc Bài Báo Gốc',
      expandArticle: 'Mở Rộng Bài Viết',
      collapseArticle: 'Thu Gọn Bài Viết',
      fullArticle: 'Bài Viết Đầy Đủ',
      viewOriginal: 'Xem Bài Báo Gốc trên Telegram.com'
    }
  };
  const t = translations[language];
  return <section id="press" ref={sectionRef} className="w-full py-24 bg-background" data-id="element-586">
      <div className="container mx-auto px-4" data-id="element-587">
        <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-16 fade-in" data-id="element-588">
          {t.featured} <span className="gradient-text" data-id="element-589">{t.press}</span>
        </h2>
        <div className="max-w-5xl mx-auto" data-id="element-590">
          <div className="bg-secondary rounded-2xl p-6 md:p-8 card-glow transform transition-all duration-500 hover:scale-105 slide-in-left" data-id="element-591">
            <div className="flex flex-col md:flex-row items-start gap-6 mb-6" data-id="element-592">
              <div className="w-full md:w-1/3 mb-6 md:mb-0" data-id="element-593">
                <div className="aspect-video bg-white rounded-lg overflow-hidden flex items-center justify-center p-4" data-id="element-594">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bEuKQaUikKg8C9R25fLZ8a/Screenshot_2025-09-08_at_11.19.51_PM.png" alt="Trung Nguyen with official" className="max-w-full max-h-full object-contain" data-id="element-595" />
                </div>
              </div>
              <div className="w-full md:w-2/3" data-id="element-596">
                <div className="flex items-center gap-3 mb-4" data-id="element-597">
                  <NewspaperIcon size={24} className="text-accent-cyan animate-pulse-slow" data-id="element-598" />
                  <h3 className="text-2xl font-display font-bold" data-id="element-599">
                    {t.newspaper}
                  </h3>
                </div>
                <h4 className="text-xl font-display font-bold mb-3 gradient-text" data-id="element-600">
                  {t.title}
                </h4>
                <p className="text-muted mb-4" data-id="element-601">{t.summary}</p>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-602">
                  <span className="text-sm text-accent-cyan" data-id="element-603">{t.date}</span>
                  <a href="https://www.telegram.com/story/opinion/columns/guest/2023/11/05/trung-nguyen-on-benefits-of-raising-immigration-funding-in-massachusetts/71343741007/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-accent-cyan hover:text-accent-violet transition-colors transform transition-all duration-300 hover:translate-x-2" data-id="element-604">
                    <span data-id="element-605">{t.readOriginal}</span>
                    <ExternalLinkIcon size={16} className="animate-bounce-slow" data-id="element-606" />
                  </a>
                </div>
              </div>
            </div>
            {/* Expand/Collapse Button */}
            <div className="mt-4 text-center" data-id="element-607">
              <button onClick={toggleArticle} className="px-6 py-2 rounded-full bg-background hover:bg-background/80 text-accent-cyan flex items-center gap-2 mx-auto transition-all duration-300 hover:shadow-lg" aria-expanded={isExpanded} aria-controls="article-content" data-id="element-608">
                <span data-id="element-609">{isExpanded ? t.collapseArticle : t.expandArticle}</span>
                {isExpanded ? <ChevronUpIcon size={18} data-id="element-610" /> : <ChevronDownIcon size={18} data-id="element-611" />}
              </button>
            </div>
            {/* Full Article (Collapsible) */}
            <div id="article-content" className={`mt-8 pt-6 border-t border-accent-cyan/20 transition-all duration-500 ease-in-out ${isExpanded ? 'block' : 'hidden'}`} data-id="element-612">
              <div className="bg-background/50 backdrop-blur-sm rounded-xl p-6" data-id="element-613">
                <h5 className="text-lg font-display font-bold mb-4 text-center text-accent-cyan" data-id="element-614">
                  {t.fullArticle}
                </h5>
                <div className="prose prose-invert max-w-none" data-id="element-615">
                  <h2 className="text-2xl font-bold mb-4" data-id="element-616">{t.title}</h2>
                  <p className="mb-4" data-id="element-617">
                    Massachusetts has long been a beacon of opportunity for
                    immigrants seeking a better life. As a first-generation
                    immigrant myself, I've witnessed firsthand the
                    transformative power of welcoming policies and adequate
                    support systems. However, our state's commitment to
                    supporting immigrant communities is at risk due to budget
                    constraints and shifting priorities.
                  </p>
                  <p className="mb-4" data-id="element-618">
                    The economic case for increasing immigration funding is
                    compelling. Immigrants in Massachusetts contribute
                    significantly to our economy, paying over $8.8 billion in
                    taxes and generating more than $28.6 billion in spending
                    power annually. Immigrant-owned businesses employ over
                    134,000 Massachusetts residents and generate billions in
                    revenue.
                  </p>
                  <p className="mb-4" data-id="element-619">
                    Beyond economics, immigrants enrich our communities
                    culturally and socially. They bring diverse perspectives,
                    traditions, and skills that make Massachusetts more vibrant
                    and innovative. By investing in programs that support
                    immigrant integration, language acquisition, and economic
                    empowerment, we strengthen the fabric of our entire
                    commonwealth.
                  </p>
                  <p className="mb-4" data-id="element-620">
                    The recent budget cuts to immigration services are
                    shortsighted and counterproductive. By reducing funding for
                    legal assistance, English language programs, and integration
                    services, we risk undermining the potential contributions of
                    our immigrant communities. Instead, we should view funding
                    for these programs as investments with substantial returns
                    for all Massachusetts residents.
                  </p>
                  <p className="mb-4" data-id="element-621">
                    As the state legislature considers budget allocations for
                    the coming fiscal year, I urge our representatives to
                    prioritize funding for immigrant services. Specifically, we
                    need increased support for English language programs, legal
                    assistance for those navigating our complex immigration
                    system, and workforce development initiatives that help
                    immigrants fully utilize their skills and education.
                  </p>
                  <p className="mb-4" data-id="element-622">
                    Massachusetts has a proud history of supporting immigrants,
                    from the Irish and Italians of the 19th century to more
                    recent arrivals from Latin America, Asia, and Africa. By
                    continuing this tradition and strengthening our commitment
                    to immigrant communities, we not only honor our past but
                    invest in our future prosperity.
                  </p>
                  <p className="mb-4" data-id="element-623">
                    The data is clear: supporting immigrant integration through
                    targeted funding is not charity; it's a strategic investment
                    with significant returns. Every dollar spent on English
                    language programs, for example, yields multiple dollars in
                    increased earning power and tax contributions. Similarly,
                    funding for legal assistance helps immigrants secure stable
                    status, which in turn enables them to pursue better jobs,
                    education, and housing.
                  </p>
                  <p className="mb-4" data-id="element-624">
                    As we emerge from the economic challenges of recent years,
                    it's essential that we make wise investments that strengthen
                    our communities and boost our economy. Increasing funding
                    for immigrant services is precisely such an investment—one
                    that will pay dividends for generations to come.
                  </p>
                  <p className="mb-4" data-id="element-625">
                    I call on our state leaders to recognize the immense value
                    that immigrants bring to Massachusetts and to back that
                    recognition with meaningful financial support. By doing so,
                    we can ensure that Massachusetts remains a place where
                    immigrants can thrive and contribute to our shared
                    prosperity.
                  </p>
                  <p className="italic text-right" data-id="element-626">
                    - Trung Nguyen, The Telegram & Gazette, November 5, 2023
                  </p>
                </div>
                <div className="mt-8 text-center" data-id="element-627">
                  <a href="https://www.telegram.com/story/opinion/columns/guest/2023/11/05/trung-nguyen-on-benefits-of-raising-immigration-funding-in-massachusetts/71343741007/" target="_blank" rel="noopener noreferrer" className="gradient-button px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 shiny-button" data-id="element-628">
                    {t.viewOriginal}
                    <ExternalLinkIcon size={16} data-id="element-629" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default FeaturedPress;